import React, { useMemo, useState, useEffect } from 'react';
import './manageEmployee.css';
import EmployeeForm from './EmployeeForm';
import EmployeeInfoModal from './employeeInfoModal'; // Import EmployeeInfoModal
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';

const ManageProject = ({ adminIds, loggedInEmployeeId, empRole }) => {
  // TODO : Admin should assign Manager. Need to add something so that admin can choose Manager from user.

  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    //console.log("Employee IDs managed by the logged-in user:", adminIds);
  }, [adminIds]);

  useEffect(() => {
    //console.log("Admin Id is:", loggedInEmployeeId);
  }, [loggedInEmployeeId]);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState(null); // State to hold employee data for editing
  const [loading, setLoading] = useState(true); // Loading state
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [roleFilter, setRoleFilter] = useState('all'); // all | admin | user
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setLoading(true);
    const fetchEmployees = async () => {
      try {
        const response = empRole === 'SuperAdmin'
          ? await axiosInstance.get('/admin/superadmin/employees')
          : await axiosInstance.get('/admin/employees');
        // const  allRequests = await axiosInstance.get('/admin/registrationReq');
        // console.log("the regisstration request are", allRequests);
        if (empRole !== 'SuperAdmin') {
          const idSet = new Set((adminIds || []).map((x) => Number(x)).filter((n) => Number.isFinite(n)));
          setEmployees(response.data.filter(employee => idSet.has(Number(employee.emp_id)))); // Filter employees based on adminIds
        } else {
          setEmployees(response.data); // SuperAdmin sees all (including inactive)
        }
      } catch (error) {
        // Check if error response is available
        if (!error.response) {
          // Network error or no response
          toast.error("Looks like you are offline. Please check your internet connectivity.");
        } else {
          console.error('Error fetching employees:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [adminIds, empRole]); // Add empRole as a dependency


  const handleEmployeeClick = async (emp_id) => {
    try {
      const response = await axiosInstance.get(`/admin/employeeInfo/${emp_id}`);
      setSelectedEmployee(response.data);
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
  };

  const closeModal = () => {
    setSelectedEmployee(null);
  };

  const handleAddEmployeeClick = () => {
    setEmployeeToEdit(null); // Reset employeeToEdit state for adding new employee
    setShowEmployeeForm(true);
  };

  const handleCloseEmployeeForm = () => {
    setShowEmployeeForm(false);
    setEmployeeToEdit(null); // Clear employeeToEdit state when form is closed
  };

  const handleEditEmployee = (emp_id) => {
    // Find the selected employee from the employees state array
    const selectedEmp = employees.find(employee => employee.emp_id === emp_id);
    if (selectedEmp) {
      setEmployeeToEdit(selectedEmp); // Set employeeToEdit state for editing
      setShowEmployeeForm(true); // Show the employee form
    } else {
      alert(`Employee with ID ${emp_id} not found`);
    }
  };

  const handleEmployeeFormSubmit = async (employeeData) => {
    try {
      if (employeeToEdit) {
        if (empRole === 'SuperAdmin') {
          const payload = {
            // employee_info / personal_info
            p_name: employeeData.name,
            p_contact: employeeData.phone_number,
            p_address: employeeData.living_address,
            work_location: employeeData.current_work_location,
            emp_mail: employeeData.email,
            emp_role: employeeData.role,
            is_active: employeeData.is_active,
            manager_id: employeeData.manager_id,
            emp_designation: employeeData.designation,
            emp_joining_date: employeeData.joining_date,
            dept_id: employeeData.department,

            // registration_approval (full profile)
            full_name: employeeData.name,
            admission_date: employeeData.admission_date || employeeData.joining_date,
            cost_center: employeeData.cost_center,
            address: employeeData.address,
            postal_code: employeeData.postal_code,
            locality: employeeData.locality,
            nationality: employeeData.nationality,
            place_of_birth: employeeData.place_of_birth,
            date_of_birth: employeeData.date_of_birth,
            gender: employeeData.gender,
            marital_status: employeeData.marital_status,
            taxpayer_number: employeeData.taxpayer_number,
            social_security_number: employeeData.social_security_number,
            beneficiary_number: employeeData.beneficiary_number,
            private_mobile: employeeData.private_mobile,
            hf_cell_phone: employeeData.hf_cell_phone,
            private_email: employeeData.private_email,
            hf_email: employeeData.hf_email,
            emergency_contact_mobile: employeeData.emergency_contact_mobile,
            emergency_contact_type: employeeData.emergency_contact_type,
            iban: employeeData.iban,
            swift: employeeData.swift,
            citizen_card_no_passport: employeeData.citizen_card_no_passport,
            expiry_date_cc_passport: employeeData.expiry_date_cc_passport,
            driving_license_no: employeeData.driving_license_no,
            categories: employeeData.categories,
            driving_license_issue_date: employeeData.driving_license_issue_date,
            driving_license_validity_date: employeeData.driving_license_validity_date,
            no_of_dependents: employeeData.no_of_dependents,
            hf_function: employeeData.hf_function,
            profession: employeeData.profession || employeeData.designation,
            academic_qualifications: employeeData.academic_qualifications,
            remarks: employeeData.remarks,
            company_id: employeeData.company_id,
            branch_id: employeeData.branch_id,
            emp_sap_id: employeeData.emp_sap_id || employeeToEdit.emp_sap_id,
          };
          await axiosInstance.put(`/admin/superadmin/employees/${employeeToEdit.emp_id}`, payload);
        } else {
          // Edit existing employee (non-superadmin)
          await axiosInstance.put(`/admin/employees/${employeeToEdit.emp_id}`, employeeData);
        }
      } else {
        // Add new employee
        await axiosInstance.post('/admin/employee/add', employeeData);
      }

      alert('Employee saved successfully');
      setShowEmployeeForm(false); // Close the employee form modal

      // Re-fetch list (different endpoints/filters per role)
      const refreshed = empRole === 'SuperAdmin'
        ? await axiosInstance.get('/admin/superadmin/employees')
        : await axiosInstance.get('/admin/employees');
      if (empRole === 'SuperAdmin') {
        setEmployees(refreshed.data);
      } else {
        setEmployees(refreshed.data.filter(employee => adminIds.includes(employee.emp_id)));
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      // Handle error here, show an error message, etc.
    }
  };
  //console.log("employeeData", selectedEmployee);

  const handleEditIconClick = (event, emp_id) => {
    event.stopPropagation(); // Prevents the row click event from firing
    handleEditEmployee(emp_id);
  };
  //console.log(selectedEmployee);
  const handleRegistrationRequests = () => {
    navigate('/registrationRequests');
  };

  const handleEmployeeHierarchy = () => {
    navigate('/employee-hierarchy');
  };

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return employees.filter((e) => {
      const isActive = Boolean(e.is_active);

      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;

      if (roleFilter !== 'all') {
        const role = (e.emp_role || '').toLowerCase();
        // Treat SuperAdmin as "admin" bucket for filtering UX
        const isAdminBucket = role === 'admin' || role === 'superadmin';
        if (roleFilter === 'admin' && !isAdminBucket) return false;
        if (roleFilter === 'user' && isAdminBucket) return false;
      }

      if (q) {
        const hay = `${e.p_name || ''} ${e.emp_mail || ''} ${e.p_mail || ''} ${e.emp_sap_id || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [employees, statusFilter, roleFilter, searchTerm]);


  return (
    <div className="emplist-container">
      <div className="employee-list">
        <h2 className='employee-table-title'>{t("manageEmp_title")}</h2>
        {/* <button className="addEmp" onClick={handleAddEmployeeClick}>{t("manageEmp_addEmpbtn")}</button> */}
        <div className="manageEmployeesTopActions">
          {empRole === 'SuperAdmin' && (
            <button className="regReq" onClick={handleRegistrationRequests}>
              {t("Registration Requests")}
            </button>
          )}
          {(empRole === 'Admin' || empRole === 'SuperAdmin') && (
            <button className="hierarchyBtn" onClick={handleEmployeeHierarchy}>
              {t("Employee Hierarchy")}
            </button>
          )}
        </div>

        <div className="manageEmployeesFilters">
          <div className="filterGroup">
            <label className="filterLabel">{t("Status") || 'Status'}</label>
            <select
              className="filterSelect"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="active">{t("Active") || 'Active'}</option>
              <option value="inactive">{t("Inactive") || 'Inactive'}</option>
              <option value="all">{t("All") || 'All'}</option>
            </select>
          </div>

          <div className="filterGroup">
            <label className="filterLabel">{t("Role") || 'Role'}</label>
            <select
              className="filterSelect"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">{t("All") || 'All'}</option>
              <option value="admin">{t("Admin") || 'Admin'}</option>
              <option value="user">{t("User") || 'User'}</option>
            </select>
          </div>

          <div className="filterGroup searchGroup">
            <label className="filterLabel">{t("Search") || 'Search'}</label>
            <input
              className="filterSearch"
              placeholder={t("Search name / email / SAP ID") || 'Search name / email / SAP ID'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {showEmployeeForm && (
          <EmployeeForm
            onClose={handleCloseEmployeeForm}
            onSubmit={handleEmployeeFormSubmit}
            loggedInEmployeeId={loggedInEmployeeId}
            employeeToEdit={employeeToEdit}
            isSuperAdmin={empRole === 'SuperAdmin'}

          />
        )}
        <div style={{ overflowX: 'auto' }}>
          <table className='empTable'>
            <thead>
              <tr>
                <th>{t("ID")}</th>
                <th className="nameCol">{t("attendance_name")}</th>
                <th>{t("Email Id")}</th>
                {empRole === 'SuperAdmin' && <th>Role</th>}
                {empRole === 'SuperAdmin' && <th>Active</th>}
                {empRole === 'SuperAdmin' && <th>{t("manageEmp_editEmp") || 'Edit'}</th>}
              </tr>
            </thead>
            <tbody style={{ background: '#fff' }}>
              {loading ? (
                <tr>
                  <td className="table-data-row"></td>
                  <td className="employee-table-Spinner table-data-row">
                    <Spinner animation="border" role="status">
                      <span className=""></span>
                    </Spinner><br />
                    <span className="mt-2">{t('Please wait your data is loading')}</span>
                  </td>
                  <td className="table-data-row"></td>
                </tr>
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td className="table-data-row"></td>
                  <td className="table-data-row">
                    <Alert variant="light" className='employee-table-Alert'>
                      {t("No employees found")}
                    </Alert>
                  </td>
                  <td className="table-data-row"></td>
                </tr>
              ) : (
                filteredEmployees.map((employee, idx) => (
                  <tr
                    className="employee-item"
                    key={employee.emp_id ? `emp-${employee.emp_id}` : `row-${employee.p_info_id ?? idx}`}
                    onClick={() => handleEmployeeClick(employee.emp_id)}
                  >
                    <td className="employee-id">{employee.emp_sap_id}</td>
                    <td className="employee-name">{employee.p_name}</td>
                    <td className="employee-mail">{employee.emp_mail ?? employee.p_mail}</td>
                    {empRole === 'SuperAdmin' && <td>{employee.emp_role}</td>}
                    {empRole === 'SuperAdmin' && <td>{employee.is_active ? 'Active' : 'Inactive'}</td>}
                    {empRole === 'SuperAdmin' && (
                      <td
                        className="edit-icon"
                        onClick={(event) => handleEditIconClick(event, employee.emp_id)}
                        style={{ cursor: 'pointer', textAlign: 'center' }}
                        title="Edit"
                      >
                        <i className="fas fa-pencil-alt"></i>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <EmployeeInfoModal
        show={selectedEmployee !== null}
        handleClose={closeModal}
        employee={selectedEmployee}
      />
    </div>
  );
};

export default ManageProject;
